import { Badge } from './Badge';

interface ContractStatusBadgeProps {
  status: string;
  showTooltip?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const statusConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'info' | 'primary'; description: string }> = {
  PENDING_APPROVAL: {
    label: 'Pendente',
    variant: 'warning',
    description: 'Contrato aguardando aprovação do administrador',
  },
  ACTIVE: {
    label: 'Ativo',
    variant: 'success',
    description: 'Contrato ativo - todas as operações permitidas',
  },
  SUSPENDED: {
    label: 'Suspenso',
    variant: 'warning',
    description: 'Contrato suspenso temporariamente - operações bloqueadas',
  },
  TERMINATED: {
    label: 'Encerrado',
    variant: 'error',
    description: 'Contrato encerrado permanentemente',
  },
};

export function ContractStatusBadge({ status, showTooltip = true, size = 'md' }: ContractStatusBadgeProps) {
  const config = statusConfig[status] || {
    label: status,
    variant: 'info' as const,
    description: 'Status desconhecido',
  };

  const badge = (
    <Badge variant={config.variant} size={size}>
      {config.label}
    </Badge>
  );

  if (showTooltip) {
    return (
      <div className="relative group inline-block">
        {badge}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-surface-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
          {config.description}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-surface-800" />
        </div>
      </div>
    );
  }

  return badge;
}

interface KycStatusBadgeProps {
  status: string;
  type: 'origin' | 'destination';
  showTooltip?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const kycStatusConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'info' | 'primary' }> = {
  PENDING: {
    label: 'Pendente',
    variant: 'warning',
  },
  APPROVED: {
    label: 'Aprovado',
    variant: 'success',
  },
  REJECTED: {
    label: 'Rejeitado',
    variant: 'error',
  },
};

export function KycStatusBadge({ status, type, showTooltip = true, size = 'sm' }: KycStatusBadgeProps) {
  const config = kycStatusConfig[status] || {
    label: status,
    variant: 'info' as const,
  };

  const typeLabel = type === 'origin' ? 'Origem' : 'Destino';
  const description = type === 'origin'
    ? 'KYC de origem verifica a procedência dos recursos'
    : 'KYC de destino verifica para onde os recursos podem ser enviados';

  const badge = (
    <Badge variant={config.variant} size={size}>
      {typeLabel}: {config.label}
    </Badge>
  );

  if (showTooltip) {
    return (
      <div className="relative group inline-block">
        {badge}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-surface-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
          {description}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-surface-800" />
        </div>
      </div>
    );
  }

  return badge;
}

interface ContractStatusSummaryProps {
  contractStatus: string;
  kycOriginStatus: string;
  kycDestinationStatus: string;
}

export function ContractStatusSummary({ contractStatus, kycOriginStatus, kycDestinationStatus }: ContractStatusSummaryProps) {
  return (
    <div className="flex flex-wrap gap-2">
      <ContractStatusBadge status={contractStatus} />
      <KycStatusBadge status={kycOriginStatus} type="origin" />
      <KycStatusBadge status={kycDestinationStatus} type="destination" />
    </div>
  );
}
